import Decimal from 'decimal.js';

// Read-only presentation of stored transaction data. No master data or tax engine.
export const known = value => value !== undefined && value !== null && value !== '';
const number = value => {
  if (!known(value)) return null;
  try { const result = new Decimal(value); return result.isFinite() ? result : null; } catch { return null; }
};
export function historyNumber(value, precision = 6) {
  const n = number(value);
  if (!n) return '—';
  const [integer, fraction] = n.toFixed(precision).split('.');
  const decimal = fraction?.replace(/0+$/, '');
  return integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + (decimal ? ',' + decimal : '');
}
export const historyMoney = (value, precision = 2) => number(value) ? `Rp ${historyNumber(value, precision)}` : '—';
export function historyDate(value, time = false) {
  if (!known(value)) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('id-ID', { day: '2-digit', month: 'short', year: 'numeric', ...(time ? { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' } : {}) }).format(date);
}
export function discountText(input, amount) {
  if (input?.kind === 'percent' && known(input.value)) return `${historyNumber(input.value)}%${known(amount) ? ` (${historyMoney(amount)})` : ' · nominal tidak tersimpan'}`;
  if (known(amount)) return historyMoney(amount);
  if (input?.kind === 'amount' && known(input.value)) return historyMoney(input.value) + ' per baris';
  return '—';
}
export function taxText(tax, amount) {
  if (tax?.enabled === false) return 'Tidak dikenakan';
  const rate = known(tax?.rate) ? `${historyNumber(tax.rate)}%` : 'Tarif tidak tersimpan';
  if (tax?.enabled === true || known(amount)) return `${rate} · ${historyMoney(amount)}`;
  return '—';
}
const statusLabels = { lunas: 'Lunas', utang: 'Hutang', sebagian: 'Sebagian Dibayar', partial: 'Sebagian Dibayar', batal: 'Batal', cancelled: 'Batal', retur: 'Retur', returned: 'Retur' };
export function paymentLabel(purchase) {
  if (purchase.payment_sync_state === 'unverified') return 'Belum Terverifikasi';
  return statusLabels[purchase.status] || statusLabels[purchase.payment_status] || purchase.payment_status || '—';
}
export function historyRows(purchase) {
  const snapshots = new Map((purchase.calculation?.rows || []).filter(row => row.line_id).map(row => [row.line_id, row]));
  return (purchase.items || []).map((item, index) => {
    const c = item.calculation || snapshots.get(item.line_id) || {};
    // Legacy fallbacks reproduce only the existing qty × stored cost / pieces rules.
    const legacyTotal = !purchase.calculation && !item.calculation && number(item.qty) && number(item.cost)
      ? number(item.qty).mul(item.cost).toFixed() : undefined;
    const stock = c.stock_in ?? item.stock_in ?? (number(item.qty) && number(item.pieces) ? number(item.qty).mul(item.pieces).add(number(item.bonus_qty || 0).mul(item.bonus_pieces ?? (Number(item.bonus_qty || 0) ? item.pieces : 1))).toFixed() : undefined);
    return { ...item, key: `${item.line_id || item.product_id || 'row'}-${index}`, snapshot: c,
      price: c.unit_input_cost ?? item.cost, stock,
      total: c.total ?? item.total ?? legacyTotal,
      legacyTotal: !known(c.total) && !known(item.total) && known(legacyTotal),
      derivedStock: !known(c.stock_in) && !known(item.stock_in) && known(stock),
    };
  });
}
export function purchaseReturnProgress(purchase, lineIndex) {
  const original = purchase.items?.[lineIndex] || {};
  const paidTotal = Number(original.qty || 0), bonusTotal = Number(original.bonus_qty || 0);
  let paidQty = 0, bonusQty = 0, stockQty = 0;
  for (const record of purchase.returns || []) for (const returned of record.items || []) {
    if (returned.line_index !== lineIndex) continue;
    const returnedQty = Number(returned.qty || 0);
    let explicitBonus = Number(returned.bonus_qty || 0), explicitPaid = returnedQty;
    if (!Object.prototype.hasOwnProperty.call(returned, 'bonus_qty') && bonusTotal > 0) {
      explicitPaid = Math.min(returnedQty, Math.max(0, paidTotal - paidQty));
      explicitBonus = Math.max(0, returnedQty - explicitPaid);
    }
    paidQty += explicitPaid; bonusQty += explicitBonus;
    const paidPieces = Number(original.pieces || 1);
    const bonusPieces = Number(original.bonus_pieces || (bonusTotal > 0 ? paidPieces : 1));
    stockQty += Number(returned.stock_qty ?? (explicitPaid * paidPieces + explicitBonus * bonusPieces));
  }
  return { paidQty, bonusQty, stockQty };
}
export function historySummary(purchase) {
  const s = purchase.calculation?.summary || {};
  const additionalCost = s.additional_cost ?? purchase.additional_cost?.amount;
  const additionalLabel = purchase.additional_cost?.treatment === 'capital_only' ? 'Biaya Tambahan · Modal saja' : 'Biaya Tambahan · Tagihan supplier';
  const fields = [['Subtotal Barang', s.subtotal], ['Diskon Awal Item', s.discount_before],
    ['Diskon Faktur Sebelum PPN', s.invoice_discount_before], ['Nilai Bersih Sebelum PPN', s.net],
    ['PPN', s.tax], ['Diskon Akhir Item', s.discount_after], ['Diskon Faktur Setelah PPN', s.invoice_discount_after],
    [additionalLabel, Number(additionalCost || 0) > 0 ? additionalCost : undefined],
    ['Bon / Utang Tambahan', Number(purchase.debt_charge?.amount || s.debt_charge || 0) > 0 ? (purchase.debt_charge?.amount ?? s.debt_charge) : undefined],
    ['Biaya Pengiriman', purchase.shipping_cost], ['Biaya Lain', purchase.other_cost]];
  // Payment amounts are never inferred from a mutable Lunas/Utang label.
  return [...fields.filter(([, value]) => known(value)), ['Total Pembelian', purchase.total],
    ['Jumlah Terbayar', purchase.paid_amount], ['Sisa Hutang', purchase.remaining_debt ?? purchase.debt_amount]];
}
export function historyEvents(purchase) {
  return [['Transaksi dibuat', purchase.created_at], ['Diposting', purchase.posted_at], ['Diperbarui', purchase.updated_at]].filter(([, value]) => known(value));
}
